import axios from "axios";
import {
  clearSession,
  getAccessToken,
  getRefreshToken,
  isTokenExpired,
  saveTokens,
  setSessionEndReason,
} from './authStorage';

const rawApiBaseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL;

if (!rawApiBaseUrl || !rawApiBaseUrl.trim()) {
  throw new Error(
    'REACT_APP_API_URL (ou REACT_APP_API_BASE_URL) não definida. Configure no arquivo .env antes de iniciar a aplicação.'
  );
}

const API_BASE_URL = rawApiBaseUrl.trim().replace(/\/+$/, '');

const api = axios.create({
  baseURL: API_BASE_URL
});

// Interceptador de requisição: permite ignorar auth quando necessário
api.interceptors.request.use(
  (config) => {
    const skipAuth = config?.skipAuth;
    if (skipAuth) {
      if (config.headers?.Authorization) {
        delete config.headers.Authorization;
      }
      return config;
    }

    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
      // Guarda qual token foi usado: no 401, diferencia "token vencido" de "outra aba já renovou".
      config.sentAccessToken = accessToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let refreshInFlight = null;

// Renova o token de acesso com o refresh (POST /users/token/refresh/). Uma renovação por vez — também
// entre abas, via Web Locks — e, se outra aba/requisição já renovou o token que venceu (`staleAccessToken`),
// reaproveita o novo em vez de gastar o refresh de novo (com rotação, o refresh antigo deixa de valer).
export const refreshAccessToken = (staleAccessToken = getAccessToken()) => {
  if (refreshInFlight) return refreshInFlight;

  const runRefresh = async () => {
    const currentAccessToken = getAccessToken();
    if (currentAccessToken && currentAccessToken !== staleAccessToken && !isTokenExpired(currentAccessToken)) {
      return currentAccessToken;
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error('Sessão sem refresh token.');

    const response = await api.post(
      'users/token/refresh/',
      { refresh: refreshToken },
      // Rota pública: um Bearer vencido no header faria a API recusar a própria renovação.
      { skipAuth: true, skip401Redirect: true, timeout: 15000 },
    );
    const { access, refresh } = response.data || {};
    if (!access) throw new Error('Renovação sem token de acesso.');
    saveTokens({ access, refresh });
    return access;
  };

  const lockedRefresh = navigator.locks?.request
    ? navigator.locks.request('siapen-token-refresh', runRefresh)
    : runRefresh();

  refreshInFlight = lockedRefresh.finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

// Texto do `detail` que o backend devolve (hoje com 403) quando o token ainda é válido, mas o usuário já
// foi deslogado no servidor — ex.: logout ou inatividade em outra aba/navegador com a mesma conta.
const LOGGED_OUT_DETAIL = 'must be logged in';

const parseResponseData = (data) => {
  if (typeof data !== 'string') return data;
  try {
    return JSON.parse(data);
  } catch (parseError) {
    return data;
  }
};

// Resposta que significa "o servidor não aceita mais esta sessão". Só vale para requisições que levaram
// o nosso token: 'logged_out' = usuário deslogado no servidor; 'token_invalid' = token vencido/inválido.
// Os demais 401/403 (OAB, senha errada, sem permissão) continuam com quem chamou.
const resolveSessionRejection = (error) => {
  const response = error?.response;
  if (!response || !error?.config?.sentAccessToken) return null;

  const data = parseResponseData(response.data);
  const detail = typeof data === 'string' ? data : (typeof data?.detail === 'string' ? data.detail : '');
  if ((response.status === 401 || response.status === 403) && detail.toLowerCase().includes(LOGGED_OUT_DETAIL)) {
    return 'logged_out';
  }
  if (response.status === 401 && data?.code === 'token_not_valid') return 'token_invalid';
  return null;
};

// Limite de requisições (429): o backend trava por endpoint por alguns minutos. O interceptor dispara
// este evento e o RateLimitNotice (montado no App.js) mostra um aviso só, mesmo com várias chamadas barradas.
export const RATE_LIMIT_EVENT = 'siapen:rate-limit';
const RATE_LIMIT_MESSAGE = 'Muitas requisições em pouco tempo. Aguarde alguns minutos e tente novamente.';

// Segundos até liberar: do texto do backend ("Expected available in 306 seconds.") ou do header Retry-After.
const resolveRetryAfterSeconds = (response, data) => {
  const detail = typeof data === 'string' ? data : (typeof data?.detail === 'string' ? data.detail : '');
  const match = detail.match(/(\d+)\s*(?:seconds?|segundos?)/i);
  if (match) return Number(match[1]);
  const header = Number(response?.headers?.['retry-after']);
  return Number.isFinite(header) && header > 0 ? header : null;
};

// Interceptador de resposta: no 401 tenta renovar o token uma vez e repete a requisição;
// se não der, encerra a sessão e volta pro login. Sessão recusada pelo servidor encerra em qualquer
// chamada, inclusive nas com skip401Redirect (que só existem pra 401 de regra de negócio).
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const config = error?.config;
    const skip401Redirect = config?.skip401Redirect;

    if (status === 429) {
      const data = parseResponseData(error.response.data);
      const retryAfterSeconds = resolveRetryAfterSeconds(error.response, data);
      // Texto em português pra quem exibe o erro da requisição (toasts das telas).
      error.response.data = { ...(data && typeof data === 'object' ? data : {}), detail: RATE_LIMIT_MESSAGE };
      // skipRateLimitNotice: telas com aviso próprio (ex.: login, que mostra a contagem regressiva).
      if (!config?.skipRateLimitNotice) {
        window.dispatchEvent(new CustomEvent(RATE_LIMIT_EVENT, { detail: { retryAfterSeconds } }));
      }
      return Promise.reject(error);
    }

    const sessionRejection = resolveSessionRejection(error);

    if (!sessionRejection && (status !== 401 || skip401Redirect)) {
      return Promise.reject(error);
    }

    // Usuário deslogado no servidor não tem o que renovar (o refresh só renova sessão de usuário logado).
    if (
      sessionRejection !== 'logged_out'
      && config && !config.skipAuth && !config.retriedAfterRefresh && getRefreshToken()
    ) {
      try {
        await refreshAccessToken(config.sentAccessToken);
        config.retriedAfterRefresh = true;
        return api(config);
      } catch (refreshError) {
        // Refresh vencido/recusado: segue pro logout abaixo.
      }
    }

    // O servidor já recusou a sessão: não há o que invalidar na rota de logout, só limpa e volta pro login.
    // O aviso na tela de login só aparece se havia sessão (a requisição levou o nosso token).
    if (config?.sentAccessToken) {
      setSessionEndReason(sessionRejection === 'logged_out' ? 'ended' : 'expired');
    }
    clearSession();
    window.location.href = '/login';

    return Promise.reject(error);
  }
);

export default api;
